import { type TFunction } from "i18next";
import { type ReactElement } from "react";
import { z } from "zod";

export type TConfigurationSchema =
  | TAzureConfigurationSchema
  | TGoogleConfigurationSchema
  | TJumpCloudConfigurationSchema
  | TMicrosoftAdConfigurationSchema
  | TOktaConfigurationSchema
  | TOneLoginConfigurationSchema;

export type TMicrosoftAdConfigurationSchema = {
  additional_base_dns?: string[];
  base_dn: string;
  options?: {
    ssl: {
      ca_file_data: string;
      enabled: boolean;
    };
  };
  password: string;
  server: string;
  username: string;
};

export type TOktaServiceJktConfiguration = {
  d: string;
  dp: string;
  dq: string;
  e: string;
  kid: string;
  kty: string;
  n: string;
  p: string;
  q: string;
  qi: string;
};

type TAzureConfigurationSchema = {
  clientId: string;
  options: {
    emailFieldName: string;
  };
  tenant: string;
} & ({ nonceId: string; nonceValue: string } | { secret: string });

type TGoogleConfigurationSchema = {
  credentialSubject: string;
  serviceAccountKey: {
    authProviderX509CertUrl: string;
    authUri: string;
    clientEmail: string;
    clientId: string;
    clientX509CertUrl: string;
    privateKey: string;
    privateKeyId: string;
    projectId: string;
    tokenUri: string;
    type: string;
  };
};

type TJumpCloudConfigurationSchema = {
  token: string;
  url: string;
};

type TOktaConfigurationSchema = {
  client_id: string;
  jwk: TOktaServiceJktConfiguration;
  options: {
    manager_email_field_name?: string;
  };
  orgUrl: string;
};

type TOneLoginConfigurationSchema = {
  clientId: string;
  clientSecret: string;
  options: {
    manager_field_name?: string;
  };
  region: string;
};

export const DIRECTORIES = ["google", "okta", "azureAD", "onelogin", "jumpcloud", "active_directory", "ldap"] as const;
export enum DirectoryConfigurationStatus {
  CONNECTED = "connected",
  DIRECTORY_PREVIEW_DONE = "directoryPreviewDone",
  DIRECTORY_PREVIEW_FAILED = "directoryPreviewFailed",
  DIRECTORY_PREVIEW_WITH_MEMBERS_DONE = "directoryPreviewWithMembersDone",
  DIRECTORY_PREVIEW_WITH_MEMBERS_FAILED = "directoryPreviewWithMembersFailed",
  FAILED_SYNCING = "failedSyncing",
  INITIALIZING = "initializing",
  PENDING = "pending",
}

export enum DirectorySyncMode {
  FULL = "full",
  PARTIAL = "partial",
}

export interface IConfigForm {
  enableSave: boolean;
  error?: string;
  inEditMode: boolean;
  onSave: (
    configuration: TConfigurationSchema,
    isDirectManagerSource?: boolean,
    agentTokenId?: null | string,
    toSnakeCase?: boolean,
  ) => Promise<void>;
  shouldReset?: boolean;
}

export type TConfigurationCreateBody = {
  agentToken?: null | string;
  directoryConfiguration: {
    configuration: Record<string, unknown>;
    directory: TDirectory;
    isDirectManagerSource?: boolean;
  };
  displayName: string;
};

export type TConfigurationUpdateBody =
  | {
    agentToken?: null | string;
    directoryConfiguration: {
      configuration: Record<string, unknown>;
      directory: TDirectory;
      isDirectManagerSource?: boolean;
    };
  }
  | {
    isDirectManagerSource: boolean;
  };

export type TDirectory = (typeof DIRECTORIES)[number];

export type TDirectorySchema = {
  directory: TDirectory;
  isDirectManagerSource?: boolean;
};

export const updateDirectoryConfigurationSchema = z.object({
  agentToken: z.string().nullable(),
  directoryConfiguration: z.object({
    configuration: z.record(z.string(), z.unknown()),
    directory: z.literal(DIRECTORIES),
    isDirectManagerSource: z.boolean().default(false),
  }),
});
export type UpdateDirectoryConfiguration = z.infer<typeof updateDirectoryConfigurationSchema>;

export const createDirectoryConfigurationSchema = updateDirectoryConfigurationSchema.extend({
  displayName: z.string().min(2).max(50),
});
export type CreateDirectoryConfiguration = z.infer<typeof createDirectoryConfigurationSchema>;

export const getBaseConfigurationFormSchema = (
  t: TFunction<"translation", "pages.directoryConfiguration.setupConnectionStep.configurationForm">,
  isDisplayNameTaken?: (value: string) => boolean,
) =>
  z.object({
    agentToken: z.string().nullish(),
    displayName: z
      .string()
      .min(2, { error: t("common.displayName.errors.nameLength") })
      .max(50, { error: t("common.displayName.errors.nameLength") })
      .refine((value) => (isDisplayNameTaken ? !isDisplayNameTaken(value) : true), {
        message: t("common.displayName.errors.nameTaken"),
      }),
    isDirectManagerSource: z.boolean(),
  });

export type DirectoryConfigurationFormProps = {
  fixedDisplayName?: string;
  isDisplayNameTaken: (value: string) => boolean;
  isSubmitting: boolean;
  setIsDirty: (value: boolean) => void;
  setSubmitButton: (value?: React.ReactNode) => void;
  submit: (data: CreateDirectoryConfiguration) => void;
  submitButton: {
    icon?: ReactElement<unknown>;
    text: string;
  };
  submitError?: Error | null;
};
